<script setup lang="ts">
/**
 * /how-to-prepare-for-an-interview — the guide for the interview cluster: how
 * to prepare, common questions and sample answers, the STAR method, what to
 * ask, the thank-you email.
 *
 * It is the step after the other two guides, and its job for the product is to
 * send people back one step: the interview is about the CV, so tailoring the
 * CV (/ats-check) and, for those not invited yet, building one
 * (/free-cv-builder) are the offers on the page.
 *
 * Carries FAQPage and HowTo. The HowTo covers only the seven preparation steps,
 * which really are one procedure in order; the question bank is not part of it.
 */
import { INTERVIEW_STEP_IDS } from '~/constants/guidePages'

const { t } = useI18n()
const { faqPageNode, howToNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/jak-se-pripravit-na-pohovor', de: '/vorbereitung-vorstellungsgespraech', pl: '/jak-przygotowac-sie-do-rozmowy-kwalifikacyjnej' } })

/** Answered in prose on the page, and mirrored into FAQPage structured data. */
const FAQ_IDS = ['wear', 'early', 'length', 'nerves', 'notes', 'bring', 'hear', 'thankYou'] as const

const faqItems = computed(() =>
  FAQ_IDS.map(id => ({
    id,
    question: t(`interviewGuide.faq.${id}.q`),
    answer: t(`interviewGuide.faq.${id}.a`),
  })),
)

const extraNodes = computed(() => [
  faqPageNode('/how-to-prepare-for-an-interview', faqItems.value),
  howToNode('/how-to-prepare-for-an-interview', {
    name: t('interviewGuide.heading'),
    description: t('interviewGuide.short.text'),
    // Sum of the per-step estimates rendered on the page.
    totalTime: 'PT3H40M',
    steps: INTERVIEW_STEP_IDS.map(id => ({
      name: t(`interviewGuide.steps.items.${id}.title`),
      text: t(`interviewGuide.steps.items.${id}.text`),
    })),
  }),
])
</script>

<template>
  <MarketingFeaturePage
    seo-key="interviewGuide"
    path="/how-to-prepare-for-an-interview"
    :breadcrumb-title="t('interviewGuide.heading')"
    :extra-nodes="extraNodes"
  >
    <MarketingInterviewGuide />

    <!-- English only: UK public-sector interviews score the same behaviours and values as the application -->
    <MarketingUkToolsNote
      heading="Interviewing for the UK Civil Service or the NHS?"
      text="Civil Service interviews score the same behaviours as the application, and NHS interviews go back over your supporting statement and the NHS values. Strong written examples are your interview answers, half prepared."
    />

    <!-- The guide's advice, done for one posting by the interview prep -->
    <MarketingFeatureVideo video="interview" />

    <MarketingFaqSection
      :title="t('interviewGuide.faq.title')"
      :items="faqItems"
      name="interview-guide-faq"
    />
  </MarketingFeaturePage>
</template>
